import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Constant } from 'src/app/constant/constant';
import { EcryptServiceService } from 'src/app/core/auth/EcryptionService/ecrypt-service.service';
import { environment } from 'src/environments/environment';
import { ConfiguredTourStateResponse, TourResponse } from '../models/tour.model';

@Injectable({ providedIn: 'root' })
export class TourApiService {

  private readonly baseUrl = environment.serverAPI4 + '/api/checklists';
  private readonly tourConfigUrl = environment.serverAPI9 + '/api/tourConfig';

  constructor(
    private readonly http: HttpClient,
    private readonly encryptionService: EcryptServiceService,
  ) {}

  startTour(): Observable<TourResponse> {
    return this.http.get<TourResponse>(`${this.baseUrl}/tour/start`, { headers: this.buildHeaders() });
  }

  markInProgress(stateId: number): Observable<any> {
    return this.http.put(`${this.baseUrl}/tour/items/${stateId}/progress`, {}, { headers: this.buildHeaders() });
  }

  markCompleted(stateId: number, remarks?: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/tour/items/${stateId}/complete`, { remarks: remarks || null }, { headers: this.buildHeaders() });
  }

  markSkipped(stateId: number, remarks?: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/tour/items/${stateId}/skip`, { remarks: remarks || null }, { headers: this.buildHeaders() });
  }

  // ─── Configured tour state ──────────────────────────────────────────────────
  // GET /api/tourConfig/{tourId}/state
  // Returns (and eagerly initialises as NOT_STARTED) one state record per tour step.
  loadConfiguredTourState(tourId: number): Observable<ConfiguredTourStateResponse> {
    return this.http.get<ConfiguredTourStateResponse>(
      `${this.tourConfigUrl}/${tourId}/state`,
      { headers: this.buildHeaders() },
    );
  }

  // GET /api/tourConfig?active=1 — list of plans/configs available for the current user
  getActiveTourConfigs(): Observable<any[]> {
    return this.http.get<any[]>(`${this.tourConfigUrl}?active=1`, { headers: this.buildHeaders() });
  }

  private buildHeaders(): HttpHeaders {
    const tenantId = this.resolveTenantId();
    let headers = new HttpHeaders();
    if (tenantId) {
      headers = headers.set('X-Tenant-Id', tenantId).set('x-tenant', tenantId);
    }
    return headers;
  }

  private resolveTenantId(): string {
    const rawTenant = localStorage.getItem(Constant.enterPriseKey);
    if (!rawTenant) {
      return '';
    }

    // Strategy 1: decrypt() uses key+IV parsed AES (ECB mode) — matches encrypt()
    try {
      const normalized = rawTenant
        .replace(/xMl3Jk/g, '+')
        .replace(/Por21Ld/g, '/')
        .replace(/Ml32/g, '=');
      const result = this.encryptionService.decrypt(normalized);
      if (result) { return result; }
    } catch (_) { /* fall through */ }

    // Strategy 2: decryptionAES() uses simple passphrase AES — matches encryptionAES()
    try {
      const result = this.encryptionService.decryptionAES(rawTenant);
      if (result) { return result; }
    } catch (_) { /* fall through */ }

    return '';
  }
}
